import React, { useMemo, useState } from 'react';
import { CalendarRange, Film, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SpotVisit, CampingSpot, AppUser } from '../../types';
import { filterVisitsByPeriod } from '../../services/visitService';
import { hasReplayAccess } from '../../services/accessControl';
import { buildTripSpec, isViableTrip } from '../../services/replayEngine';
import ReplayOverlay from './ReplayOverlay';

/**
 * Delt datofilter + «Reisefilm»-inngang for reisetavla (eng-review D1: samme
 * innhold i profil-fanen OG header-inngangen). Presentasjonell og kontrollert —
 * eieren holder filtertilstanden via usePeriodFilter-hooken under, slik at
 * VisitsTab kan bruke det filtrerte utvalget også i fylkes-drilldown m.m.
 */

export function usePeriodFilter(visits: SpotVisit[]) {
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const hasPeriodFilter = !!(filterFrom || filterTo);
  const periodVisits = useMemo(() => {
    if (!hasPeriodFilter) return visits;
    const fromMs = filterFrom ? new Date(`${filterFrom}T00:00:00`).getTime() : undefined;
    const toMs = filterTo ? new Date(`${filterTo}T23:59:59.999`).getTime() : undefined;
    return filterVisitsByPeriod(visits, fromMs, toMs);
  }, [visits, filterFrom, filterTo, hasPeriodFilter]);
  return { filterFrom, setFilterFrom, filterTo, setFilterTo, hasPeriodFilter, periodVisits };
}

interface PeriodReplayControlsProps {
  filterFrom: string;
  filterTo: string;
  onFilterFromChange: (v: string) => void;
  onFilterToChange: (v: string) => void;
  hasPeriodFilter: boolean;
  /** Periodfiltrerte besøk — for liste/tavle (datofilteret over knappen). */
  periodVisits: SpotVisit[];
  /** ALLE besøk — overlayets veiviser eier sin egen periode (default: siste tur). */
  allVisits: SpotVisit[];
  spots: CampingSpot[];
  user: AppUser | null | undefined;
}

const PeriodReplayControls: React.FC<PeriodReplayControlsProps> = ({
  filterFrom, filterTo, onFilterFromChange, onFilterToChange,
  hasPeriodFilter, periodVisits, allVisits, spots, user,
}) => {
  const { t } = useTranslation();
  const [showReplay, setShowReplay] = useState(false);
  // Knappen krever bare ≥2 besøk m/ posisjon TOTALT — veiviseren velger perioden.
  const replayViable = useMemo(() => isViableTrip(buildTripSpec(allVisits).spec), [allVisits]);
  // Testfunksjon inntil videre (eier-beslutning): kun admin + aktive testere
  // ser Reisefilm. Datofilteret er generell UX og vises for alle.
  const canUseReplay = hasReplayAccess(user);

  return (
    <>
      {/* Datofilter — default alle; styrer liste + tavle (+ replay), ikke tier */}
      <div className="flex items-center gap-2 bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-2xl px-3 py-2 mx-1">
        <CalendarRange size={14} className="text-[var(--text-muted)] flex-shrink-0" />
        <input
          type="date"
          value={filterFrom}
          max={filterTo || undefined}
          onChange={e => onFilterFromChange(e.target.value)}
          aria-label={t('visitsTab.filter.from', 'Fra dato')}
          className="flex-1 min-w-0 bg-transparent text-[12px] text-[var(--text-primary)] outline-none [color-scheme:dark]"
        />
        <span className="text-2xs font-black text-[var(--text-muted)]">–</span>
        <input
          type="date"
          value={filterTo}
          min={filterFrom || undefined}
          onChange={e => onFilterToChange(e.target.value)}
          aria-label={t('visitsTab.filter.to', 'Til dato')}
          className="flex-1 min-w-0 bg-transparent text-[12px] text-[var(--text-primary)] outline-none [color-scheme:dark]"
        />
        {hasPeriodFilter && (
          <button
            type="button"
            onClick={() => { onFilterFromChange(''); onFilterToChange(''); }}
            aria-label={t('visitsTab.filter.reset', 'Vis alle besøk')}
            className="flex-shrink-0 p-1.5 -mr-1 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {hasPeriodFilter && periodVisits.length === 0 && (
        <div className="bg-[var(--bg-surface-alt)] rounded-2xl p-4 border border-[var(--border-primary)] text-center">
          <p className="text-tiny text-[var(--text-muted)]">{t('visitsTab.filter.empty', 'Ingen besøk i valgt periode')}</p>
        </div>
      )}

      {/* Reisefilm (reise-replay) — testfunksjon: kun admin + aktive testere */}
      {canUseReplay && (
      <div className="mx-1">
        <button
          type="button"
          onClick={() => replayViable && setShowReplay(true)}
          disabled={!replayViable || !user}
          className="w-full flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark disabled:bg-[var(--bg-input)] disabled:text-[var(--text-muted)] text-stone-900 py-3 rounded-xl text-tiny font-black uppercase tracking-widest transition-colors"
        >
          <Film size={15} />
          {t('visitsTab.replay.button', 'Reisefilm')}
        </button>
        {!replayViable && (
          <p className="text-2xs text-[var(--text-muted)] text-center mt-1.5">
            {t('visitsTab.replay.needTwo', 'Velg en periode med minst 2 innsjekk med posisjon')}
          </p>
        )}
      </div>
      )}

      {showReplay && user && canUseReplay && (
        <ReplayOverlay
          visits={allVisits}
          initialFrom={filterFrom}
          initialTo={filterTo}
          spots={spots}
          user={user}
          onClose={() => setShowReplay(false)}
        />
      )}
    </>
  );
};

export default PeriodReplayControls;
